import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Crown, Landmark, Lock, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

interface RadarIssue {
  id: string;
  week: string;
  title: string;
  created_at: string;
  happened?: string[];
  docket?: string[];
  takeaway?: string;
}

const weekLabel = (iso: string) =>
  new Date(iso).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });

export default function RadarArchive() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["radar-issues", !!user],
    queryFn: async () => (await api.get("/radar/issues")).data,
  });
  const issues: RadarIssue[] = data?.issues || [];
  const locked = data?.locked !== false;

  return (
    <div data-testid="radar-archive-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-[#A78BFA] mb-3">
        <Crown className="h-3.5 w-3.5" /> NewsLayer Premium
      </p>
      <h1 className="font-news font-black text-white text-4xl sm:text-5xl">Regulation Radar</h1>
      <p className="text-gray-400 text-lg mt-4 max-w-2xl">
        The weekly policy briefing for professionals — what regulators did, what's on the docket,
        and what it means. Delivered every Monday, archived here.
      </p>

      {isLoading ? (
        <p className="text-gray-500 mt-12">Loading issues…</p>
      ) : issues.length === 0 ? (
        <div className="mt-12 border border-dashed border-white/10 rounded-xl py-16 text-center" data-testid="radar-empty">
          <Landmark className="h-8 w-8 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">The first issue lands Monday.</p>
          <p className="text-gray-500 text-sm mt-1">Every weekly briefing will be archived on this page.</p>
        </div>
      ) : locked ? (
        <div className="mt-10" data-testid="radar-locked">
          <div className="bg-[#1a1030] border border-[#7C3AED]/50 rounded-xl p-6 sm:p-8 mb-8">
            <h2 className="font-news font-bold text-white text-lg md:text-lg mb-2">
              Unlock every issue with Premium
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xl mb-5">
              Regulation Radar is exclusive to Premium members — get the Monday email plus full
              access to this archive, ad-free reading and premium deep dives.
            </p>
            <Link to="/premium" data-testid="radar-upgrade-link"
              className="sparkle-btn inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#6b2fd6] text-white font-bold rounded-md px-5 py-2.5 text-sm transition-colors">
              <Crown className="h-4 w-4" /> Go Premium
            </Link>
          </div>
          <div className="space-y-3">
            {issues.map((i) => (
              <div key={i.id} data-testid={`radar-issue-locked-${i.week}`}
                className="flex items-center gap-4 bg-[#121212] border border-white/10 rounded-lg px-5 py-4">
                <Lock className="h-4 w-4 text-gray-600 shrink-0" />
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{i.week}</p>
                  <p className="text-gray-300 font-semibold truncate">{i.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-10 space-y-8" data-testid="radar-issues">
          {issues.map((i) => (
            <article key={i.id} data-testid={`radar-issue-${i.week}`}
              className="bg-[#121212] border border-[#7C3AED]/40 rounded-xl p-6 sm:p-8 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#7C3AED]" />
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#A78BFA] mb-2">
                <CalendarDays className="h-3.5 w-3.5" /> {i.week} · {weekLabel(i.created_at)}
              </p>
              <h2 className="font-news font-bold text-white text-xl sm:text-2xl">{i.title}</h2>
              {(i.happened || []).length > 0 && (
                <div className="mt-5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">What happened</p>
                  <ul className="space-y-2">
                    {i.happened!.map((h, idx) => (
                      <li key={idx} className="flex gap-3 text-gray-300 text-sm leading-relaxed">
                        <span className="font-mono text-[#A78BFA] shrink-0">0{idx + 1}</span> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(i.docket || []).length > 0 && (
                <div className="mt-5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">On the docket</p>
                  <ul className="space-y-2">
                    {i.docket!.map((d, idx) => (
                      <li key={idx} className="flex gap-3 text-gray-300 text-sm leading-relaxed">
                        <span className="text-[#00E5FF] shrink-0">→</span> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {i.takeaway && (
                <div className="mt-5 bg-white/[0.03] border border-white/10 rounded-lg p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">The takeaway</p>
                  <p className="text-gray-200 text-sm leading-relaxed">{i.takeaway}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
